import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Swords, ChevronRight, ChevronDown, Zap, ScrollText, ArrowRight, Info } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { Loader, Empty } from "../components/UI";
import { ClubCrest } from "../components/ClubCrest";
import { useEventStream } from "../lib/sse";

const CONTAINER = "mx-auto max-w-[1800px] px-4 sm:px-8 lg:px-12";

// ── Status pill (text + dot, never colour-only) ─────────────────────────────
export function StatusPill({ status }) {
  const map = {
    PLANNED: { label: "Planned", cls: "bg-slate-900/70 text-slate-300 border-slate-600/50", dot: "bg-slate-400" },
    ACTIVE: { label: "Active", cls: "bg-emerald-950/70 text-emerald-400 border-emerald-500/40", dot: "bg-emerald-400" },
    IN_PROGRESS: { label: "In Progress", cls: "bg-[#140f02]/70 text-[#F0C72A] border-[#E5B31E]/50", dot: "bg-[#F0C72A] animate-pulse" },
    SCHEDULED: { label: "Scheduled", cls: "bg-slate-800/70 text-slate-300 border-slate-600/50", dot: "bg-slate-400" },
    LEG1: { label: "Leg 1", cls: "bg-[#140f02]/60 text-[#F0C72A] border-[#E5B31E]/40", dot: "bg-[#F0C72A]" },
    LEG2: { label: "Leg 2", cls: "bg-[#140f02]/60 text-[#F0C72A] border-[#E5B31E]/40", dot: "bg-[#F0C72A]" },
    EXTRA_DUELS: { label: "Extra Duels", cls: "bg-fuchsia-950/60 text-fuchsia-300 border-fuchsia-500/40", dot: "bg-fuchsia-400 animate-pulse" },
    COMPLETED: { label: "Completed", cls: "bg-emerald-950/60 text-emerald-300 border-emerald-500/40", dot: "bg-emerald-400" },
  };
  const m = map[status] || map.SCHEDULED;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider border ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
}

// Team-points pair with explicit semantic label — never a naked football-style score.
function AggScore({ home, away, homeWin, awayWin }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`font-heading text-2xl font-black tabular-nums ${homeWin ? "text-[#E5B842]" : "text-slate-200"}`}>{home}</span>
      <span className="text-[8px] leading-tight font-mono uppercase tracking-[0.15em] text-slate-500 text-center">Agg<br />Team<br />Pts</span>
      <span className={`font-heading text-2xl font-black tabular-nums ${awayWin ? "text-[#E5B842]" : "text-slate-200"}`}>{away}</span>
    </div>
  );
}

function TieCard({ t }) {
  const homeWin = t.winner_club && t.winner_club.id === t.home_club?.id;
  const awayWin = t.winner_club && t.winner_club.id === t.away_club?.id;
  const seedBadge = (s) => (s && s < 90 ? <span className="text-slate-600 text-[9px] font-mono ml-1">#{s}</span> : null);
  return (
    <Link to={`/cup/tie/${t.id}`} data-testid={`cup-tie-${t.id}`}
      className="block rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/45"
      style={{ transition: "border-color 0.15s ease, transform 0.15s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; }}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500">
          {t.two_leg ? "Two legs" : "Single match"}{t.is_staged_demo ? " · Staged demo" : ""}
        </span>
        <StatusPill status={t.status} />
      </div>
      <div className="space-y-2">
        <TeamRow club={t.home_club} seed={seedBadge(t.home_seed)} pts={t.aggregate_home} win={homeWin} />
        <TeamRow club={t.away_club} seed={seedBadge(t.away_seed)} pts={t.aggregate_away} win={awayWin} />
      </div>
      <div className="mt-3 pt-3 border-t border-[#2b2512] flex items-center justify-between">
        {t.extra_duels_required ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-fuchsia-400"><Zap className="w-3 h-3" /> Extra Duel</span>
        ) : <span className="text-[10px] font-mono uppercase tracking-wider text-slate-600">Agg decided</span>}
        {t.winner_club ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-300"><Crown className="w-3 h-3 text-[#E5B842]" />{t.winner_club.short_name || t.winner_club.name} advances</span>
        ) : <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500">View tie <ChevronRight className="w-3 h-3" /></span>}
      </div>
    </Link>
  );
}

function TeamRow({ club, seed, pts, win }) {
  return (
    <div className="flex items-center gap-2">
      <ClubCrest club={club} size={26} />
      <span className={`flex-1 truncate font-heading font-bold uppercase text-sm ${win ? "text-[#E5B842]" : "text-slate-200"}`}>
        {club?.short_name || club?.name}{seed}
      </span>
      <span className={`font-heading text-xl font-black tabular-nums ${win ? "text-[#E5B842]" : "text-slate-100"}`}>{pts}</span>
    </div>
  );
}

export default function CupPage() {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null); // competition detail (season / federation / territory)
  const load = useCallback(() => {
    api.get("/cup").then(({ data }) => {
      setData(data);
      const id = data?.competition?.id;
      if (id && !meta) api.get(`/competitions/${id}`).then(({ data: c }) => setMeta(c)).catch(() => {});
    }).catch(() => setData({ rounds: [] }));
  }, [meta]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("cup.")) load(); });

  if (!data) return <Loader label="Loading cup…" />;
  if (!data.competition) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="Cup has not started yet." /></div>;

  const comp = data.competition;
  const cfg = comp.config || {};
  const season = meta?.season?.name;
  const fedName = meta?.federation_id ? null : null; // federation name resolved below via meta if present
  const territory = meta?.country ? meta.country.name : (meta ? "International" : null);
  const clubCount = meta?.team_count;

  return (
    <div className="animate-fade-up pb-20">
      {/* LEVEL 1 — Cup Championship Hub hero */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-gradient-to-b from-[#141007] to-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.14] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 130% at 50% -10%, rgba(212,175,55,0.22) 0%, transparent 55%)" }} />
        {/* subtle spotlight beams (CSS only) */}
        <div className="absolute left-1/2 -translate-x-1/2 top-0 opacity-[0.12] pointer-events-none" aria-hidden="true"
          style={{ width: 760, height: 320, background: "conic-gradient(from 180deg at 50% 0%, transparent 70deg, #E5B842 90deg, transparent 110deg)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <div className="flex flex-col lg:flex-row lg:items-end gap-6">
            <div className="shrink-0 w-20 h-20 rounded-2xl bg-[#000000] border border-[#E5B842]/45 flex items-center justify-center">
              <Crown className="w-10 h-10 text-[#E5B842]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-[#E5B842] font-bold">
                Domestic Cup · ArenaBook {season && <span className="text-slate-500 font-normal">· Season {season}</span>}
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none mt-2 wsbf-slant-block">{comp.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <StatusPill status={comp.status} />
                {territory && <span className="font-mono text-slate-300 inline-flex items-center gap-1.5">{meta?.country && <Flag country={meta.country} />}{territory}</span>}
                {typeof clubCount === "number" && clubCount > 0 && <span className="font-mono text-slate-400">{clubCount} clubs</span>}
                <Link to={`/competition/${comp.id}`} data-testid="cup-rules-link" className="inline-flex items-center gap-1.5 font-mono text-slate-300 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                  <ScrollText className="w-3.5 h-3.5 text-[#E5B842]" /> Teams &amp; Rules
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        {/* CUP CHAMPION — only when backend explicitly identifies one */}
        {comp.status === "COMPLETED" && data.champion && (
          <Link to={`/club/${data.champion.id}`} data-testid="cup-champion"
            className="group relative overflow-hidden flex items-center gap-5 rounded-2xl foil-border bg-gradient-to-r from-[#1a1407] to-[#070707] p-6 mb-8 hover:border-[#E5B842]/65"
            style={{ transition: "border-color 0.15s ease" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(70% 160% at 10% 0%, rgba(212,175,55,0.18) 0%, transparent 60%)" }} />
            <ClubCrest club={data.champion} size={64} />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold flex items-center gap-2"><Crown className="w-4 h-4" /> Cup Champion</div>
              <div className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1">{data.champion.name}</div>
            </div>
            <ChevronRight className="relative ml-auto w-6 h-6 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }} />
          </Link>
        )}

        {/* How ties are decided — verified rule semantics only */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-mono text-slate-500 mb-6" data-testid="cup-rules-strip">
          <span className="inline-flex items-center gap-1.5 text-[#E5B842]"><Info className="w-3.5 h-3.5" /> How ties are decided</span>
          <span>Two legs → aggregate Team Points</span>
          <span className="text-slate-700">·</span>
          <span>Level aggregate → ArenaBook Extra Duels</span>
          <span className="text-slate-700">·</span>
          <span>No away advantage{cfg.away_points_advantage === false ? "" : ""}</span>
          <span className="text-slate-700">·</span>
          <span>Final is a single match</span>
        </div>

        {/* BYES module */}
        {data.byes?.length > 0 && (
          <div data-testid="cup-byes" className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-4 mb-8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-3">Byes to the next round</div>
            <div className="flex flex-wrap gap-2">
              {data.byes.map((b) => (
                <Link key={b.id} to={`/club/${b.id}`} data-testid={`cup-bye-${b.id}`} className="inline-flex items-center gap-2 rounded-lg bg-[#161616] border border-[#2b2512] px-3 py-1.5 hover:border-[#E5B842]/45" style={{ transition: "border-color 0.15s ease" }}>
                  <ClubCrest club={b} size={22} />
                  <span className="font-heading font-bold uppercase text-sm text-slate-200">{b.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ROAD TO THE FINAL */}
        <div className="flex items-center gap-3 mb-5">
          <Trophy className="w-5 h-5 text-[#E5B842]" />
          <h2 className="font-heading text-2xl font-extrabold uppercase tracking-tight text-slate-50">Road to the Final</h2>
        </div>

        {data.rounds.length === 0 ? <Empty label="No rounds published yet." /> : (
          <div className="lg:overflow-x-auto no-scrollbar lg:pb-4" data-testid="cup-road">
            <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-3 lg:min-w-max">
              {data.rounds.map((r, i) => (
                <div key={r.code} className="contents lg:flex lg:items-center">
                  <RoundColumn round={r} isFinal={r.code === "FINAL"} />
                  {i < data.rounds.length - 1 && (
                    <>
                      <div className="hidden lg:flex items-center px-1 text-slate-700"><ArrowRight className="w-5 h-5" /></div>
                      <div className="flex lg:hidden items-center justify-center py-1 text-slate-700"><ChevronDown className="w-5 h-5" /></div>
                    </>
                  )}
                </div>
              ))}
              {/* Champion terminal node */}
              {data.champion && (
                <>
                  <div className="hidden lg:flex items-center px-1 text-slate-700"><ArrowRight className="w-5 h-5" /></div>
                  <div className="flex lg:hidden items-center justify-center py-1 text-slate-700"><ChevronDown className="w-5 h-5" /></div>
                  <div className="lg:w-[260px] lg:shrink-0 lg:self-center">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#E5B842] mb-3 text-center lg:text-left">Champion</div>
                    <Link to={`/club/${data.champion.id}`} className="flex flex-col items-center gap-3 rounded-xl foil-border bg-gradient-to-b from-[#1a1407] to-[#070707] p-6">
                      <Crown className="w-7 h-7 text-[#E5B842]" />
                      <ClubCrest club={data.champion} size={52} />
                      <span className="font-heading font-black uppercase text-center text-slate-50 leading-snug">{data.champion.name}</span>
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RoundColumn({ round, isFinal }) {
  return (
    <section data-testid={`cup-round-${round.code}`} className="lg:w-[320px] lg:shrink-0 lg:self-center">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-heading text-sm font-black uppercase tracking-wider ${isFinal ? "text-[#E5B842]" : "text-slate-200"}`}>
          {round.code === "DEMO" ? "Demo" : round.name}
        </h3>
        <span className="font-mono text-[10px] text-slate-600">{round.ties.length} tie{round.ties.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-3">
        {round.ties.map((t) => <TieCard key={t.id} t={t} />)}
      </div>
    </section>
  );
}
